import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import { sanitizeGame } from './game.slice';

// Async Thunk: Retrieve administrative users directory
export const fetchAdminUsers = createAsyncThunk(
    'adminUser/fetchAdminUsers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const { page = 1, limit = 25, search = '', role = 'ALL' } = params;
            const res = await axiosInstance.get(`/users?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&role=${role}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to retrieve directory.");
        }

        
    }
);

// Async Thunk: Modify user role security clearance level
export const updateAdminUserClearance = createAsyncThunk(
    'adminUser/updateAdminUserClearance',
    async ({ userId, role }, { rejectWithValue }) => {
        try {
            let res;
            if (role === 'admin' || role === 'superadmin' || role === 'superAdmin') {
                res = await axiosInstance.put(`/admin/users/${userId}/promote`);
            } else {
                res = await axiosInstance.put(`/users/${userId}`, { role });
            }
            if (res.data.success) {
                return { userId, role };
            }
            return rejectWithValue("Operation failed");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Override instruction rejected.");
        }
    }
);

// Async Thunk: Purge/Delete a user record permanently
export const deleteAdminUser = createAsyncThunk(
    'adminUser/deleteAdminUser',
    async (userId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete(`/users/${userId}`);
            if (res.data.success) {
                return userId;
            }
            return rejectWithValue("Purge operation rejected");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Purge protocol failed.");
        }
    }
);

// Async Thunk: Create/Register a new user identity node
export const createAdminUser = createAsyncThunk(
    'adminUser/createAdminUser',
    async (userData, { rejectWithValue }) => {
        try {
            // Register endpoint can accept role if set by admin
            const res = await axiosInstance.post('/register', userData);
            return res.data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Identity registration failed.");
        }
    }
);

// Async Thunk: Retrieve administrative orders list
export const fetchAdminOrders = createAsyncThunk(
    'adminUser/fetchAdminOrders',
    async (params = {}, { rejectWithValue }) => {
        try {
            const { page = 1, limit = 10, search = '', status = 'All' } = params;
            const res = await axiosInstance.get(`/orders?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&status=${status}`);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to retrieve orders.");
        }
    }
);

// Async Thunk: Update order status
export const updateAdminOrderStatus = createAsyncThunk(
    'adminUser/updateAdminOrderStatus',
    async ({ orderId, status }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(`/orders/${orderId}/status`, { status });
            if (res.data.success) {
                return { orderId, status, message: res.data.message };
            }
            return rejectWithValue(res.data.message || 'Failed to update order status');
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to update order status.");
        }
    }
);

// Async Thunk: Retrieve administrative activity logs list
export const fetchActivityLogs = createAsyncThunk(
    'adminUser/fetchActivityLogs',
    async (params = {}, { rejectWithValue }) => {
        try {
            const { archived = false } = params;
            const res = await axiosInstance.get(`/admin/activity-logs?archived=${archived}`);
            return res.data.activities || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to retrieve activity logs.");
        }
    }
);

// Async Thunk: Clear all activity logs
export const clearActivityLogsThunk = createAsyncThunk(
    'adminUser/clearActivityLogsThunk',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete('/admin/activity-logs');
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to clear activity logs.");
        }
    }
);

// Async Thunk: Archive all active logs
export const archiveActivityLogsThunk = createAsyncThunk(
    'adminUser/archiveActivityLogsThunk',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.patch('/admin/activity-logs/archive');
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to archive activity logs.");
        }
    }
);

// Async Thunk: Archive a single activity log entry by ID
export const archiveSingleActivityLogThunk = createAsyncThunk(
    'adminUser/archiveSingleActivityLogThunk',
    async (logId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.patch(`/admin/activity-logs/${logId}/archive`);
            if (res.data.success) {
                return logId;
            }
            return rejectWithValue("Failed to archive log entry");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to archive activity log.");
        }
    }
);

// Async Thunk: Unarchive a single activity log entry by ID
export const unarchiveSingleActivityLogThunk = createAsyncThunk(
    'adminUser/unarchiveSingleActivityLogThunk',
    async (logId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.patch(`/admin/activity-logs/${logId}/unarchive`);
            if (res.data.success) {
                return logId;
            }
            return rejectWithValue("Failed to unarchive log entry");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to unarchive activity log.");
        }
    }
);

// Async Thunk: Delete a single activity log entry by ID permanently
export const deleteSingleActivityLogThunk = createAsyncThunk(
    'adminUser/deleteSingleActivityLogThunk',
    async (logId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete(`/admin/activity-logs/${logId}`);
            if (res.data.success) {
                return logId;
            }
            return rejectWithValue("Failed to delete log entry");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to delete activity log.");
        }
    }
);

// Async Thunk: Clear all archived activity logs
export const clearArchivedActivityLogsThunk = createAsyncThunk(
    'adminUser/clearArchivedActivityLogsThunk',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete('/admin/activity-logs/archived');
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to clear archived activity logs.");
        }
    }
);

// Async Thunk: Seed activity logs with dummy data on demand
export const seedActivityLogsThunk = createAsyncThunk(
    'adminUser/seedActivityLogsThunk',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post('/admin/activity-logs/seed');
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to seed activity logs.");
        }
    }
);

// Async Thunk: Retrieve administrative complete dashboard payload in a single call
export const fetchAdminDashboardStats = createAsyncThunk(
    'adminUser/fetchAdminDashboardStats',
    async (filter, { rejectWithValue }) => {
        try {
            const url = filter ? `/admin/dashboard-stats?filter=${encodeURIComponent(filter)}` : '/admin/dashboard-stats';
            const res = await axiosInstance.get(url);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to retrieve dashboard datasets.");
        }
    }
);

// Async Thunk: Retrieve administrative contact submissions list
export const fetchAdminContacts = createAsyncThunk(
    'adminUser/fetchAdminContacts',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get('/contacts');
            return res.data.contacts || [];
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to retrieve contact inquiries.");
        }
    }
);

// Async Thunk: Delete a contact submission
export const deleteAdminContact = createAsyncThunk(
    'adminUser/deleteAdminContact',
    async (contactId, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.delete(`/contacts/${contactId}`);
            if (res.data.success) {
                return contactId;
            }
            return rejectWithValue("Purge operation rejected");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to delete contact inquiry.");
        }
    }
);

// Async Thunk: Send a reply to a contact submission
export const replyAdminContact = createAsyncThunk(
    'adminUser/replyAdminContact',
    async ({ contactId, replyText }, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post(`/contacts/${contactId}/reply`, { replyMessage: replyText });
            if (res.data.success) {
                return res.data.contact;
            }
            return rejectWithValue("Reply operation rejected");
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || "Failed to dispatch reply.");
        }
    }
);

const initialState = {
    users: [],
    pagination: { total: 0, page: 1, limit: 20, pages: 1 },
    orders: [],
    ordersPagination: { currentPage: 1, totalPages: 1, totalItems: 0, limit: 10 },
    activities: [],
    contacts: [],
    subscribers: [],
    dashboardGames: [],
    dashboardOrders: [],
    dashboardUsers: [],
    reviewsCount: 0,
    gamesCount: 0,
    stats: {},
    statusCounts: {},
    dashboardChartData: { labels: [], data: [] },
    dashboardTopSelling: [],
    loading: false,
    ordersLoaded: false,
    actionPending: false,
    error: null,
};

export const adminUserSlice = createSlice({
    name: 'adminUser',
    initialState,
    reducers: {
        clearAdminError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Directory
            .addCase(fetchAdminUsers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdminUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.users = action.payload.users || [];
                state.pagination = action.payload.pagination || { total: 0, page: 1, limit: 20, pages: 1 };
            })
            .addCase(fetchAdminUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update Clearance
            .addCase(updateAdminUserClearance.pending, (state) => {
                state.actionPending = true;
            })
            .addCase(updateAdminUserClearance.fulfilled, (state, action) => {
                state.actionPending = false;
                state.users = state.users.map(u =>
                    (u._id === action.payload.userId || u.id === action.payload.userId)
                        ? { ...u, role: action.payload.role }
                        : u
                );
            })
            .addCase(updateAdminUserClearance.rejected, (state, action) => {
                state.actionPending = false;
                state.error = action.payload;
            })

            // Purge User
            .addCase(deleteAdminUser.pending, (state) => {
                state.actionPending = true;
            })
            .addCase(deleteAdminUser.fulfilled, (state, action) => {
                state.actionPending = false;
                state.users = state.users.filter(u => u._id !== action.payload && u.id !== action.payload);
            })
            .addCase(deleteAdminUser.rejected, (state, action) => {
                state.actionPending = false;
                state.error = action.payload;
            })

            // Create User
            .addCase(createAdminUser.pending, (state) => {
                state.actionPending = true;
            })
            .addCase(createAdminUser.fulfilled, (state, action) => {
                state.actionPending = false;
                // Add newly registered identity to state list
                if (action.payload) {
                    state.users = [action.payload, ...state.users];
                }
            })
            .addCase(createAdminUser.rejected, (state, action) => {
                state.actionPending = false;
                state.error = action.payload;
            })

            // Fetch Orders
            .addCase(fetchAdminOrders.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdminOrders.fulfilled, (state, action) => {
                state.loading = false;
                state.ordersLoaded = true;
                state.orders = (action.payload.orders || []).map(order => ({
                    ...order,
                    items: (order.items || []).map(item => ({
                        ...item,
                        game: sanitizeGame(item.game)
                    }))
                }));
                state.ordersPagination = {
                    ...(action.payload.pagination || { currentPage: 1, totalPages: 1, totalItems: 0, limit: 10 }),
                    successTotalCount: action.payload.successTotalCount || 0,
                    failedTotalCount: action.payload.failedTotalCount || 0
                };
            })
            .addCase(fetchAdminOrders.rejected, (state, action) => {
                state.loading = false;
                state.ordersLoaded = true;
                state.error = action.payload;
            })

            // Update Order Status
            .addCase(updateAdminOrderStatus.pending, (state) => {
                state.actionPending = true;
            })
            .addCase(updateAdminOrderStatus.fulfilled, (state, action) => {
                state.actionPending = false;
                const { orderId, status } = action.payload;
                const index = state.orders.findIndex(o => o._id === orderId || o.id === orderId);
                if (index !== -1) {
                    state.orders[index] = { ...state.orders[index], status };
                }
            })
            .addCase(updateAdminOrderStatus.rejected, (state, action) => {
                state.actionPending = false;
                state.error = action.payload;
            })

            // Fetch Activity Logs
            .addCase(fetchActivityLogs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchActivityLogs.fulfilled, (state, action) => {
                state.loading = false;
                state.activities = action.payload;
            })
            .addCase(fetchActivityLogs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(clearActivityLogsThunk.fulfilled, (state) => {
                state.activities = [];
            })
            .addCase(clearArchivedActivityLogsThunk.fulfilled, (state) => {
                state.activities = [];
            })
            .addCase(archiveActivityLogsThunk.fulfilled, (state) => {
                state.activities = [];
            })
            .addCase(archiveSingleActivityLogThunk.fulfilled, (state, action) => {
                state.activities = state.activities.filter(a => a._id !== action.payload && a.id !== action.payload);
            })
            .addCase(unarchiveSingleActivityLogThunk.fulfilled, (state, action) => {
                state.activities = state.activities.filter(a => a._id !== action.payload && a.id !== action.payload);
            })
            .addCase(deleteSingleActivityLogThunk.fulfilled, (state, action) => {
                state.activities = state.activities.filter(a => a._id !== action.payload && a.id !== action.payload);
            })
            .addCase(seedActivityLogsThunk.fulfilled, (state, action) => {
                state.activities = action.payload.activities || [];
            })

            // Fetch Complete Dashboard Stats Single Call
            .addCase(fetchAdminDashboardStats.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdminDashboardStats.fulfilled, (state, action) => {
                state.loading = false;
                // Since the API now returns optimized stats directly:
                state.stats = action.payload.stats || {};
                state.statusCounts = action.payload.statusCounts || {};
                state.dashboardChartData = action.payload.chartData || { labels: [], data: [] };
                state.dashboardTopSelling = action.payload.topSellingGames || [];
                
                state.dashboardOrders = action.payload.latestOrders || [];
                state.dashboardUsers = action.payload.latestUsers || [];
                state.activities = action.payload.activities || [];
                state.dashboardGames = (action.payload.latestGames || []).map(g => sanitizeGame({ ...g, id: g._id || g.id }));
            })
            .addCase(fetchAdminDashboardStats.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Contacts
            .addCase(fetchAdminContacts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAdminContacts.fulfilled, (state, action) => {
                state.loading = false;
                state.contacts = action.payload;
            })
            .addCase(fetchAdminContacts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Delete Contact
            .addCase(deleteAdminContact.pending, (state) => {
                state.actionPending = true;
            })
            .addCase(deleteAdminContact.fulfilled, (state, action) => {
                state.actionPending = false;
                state.contacts = state.contacts.filter(c => c._id !== action.payload && c.id !== action.payload);
            })
            .addCase(deleteAdminContact.rejected, (state, action) => {
                state.actionPending = false;
                state.error = action.payload;
            })
            // Reply Contact
            .addCase(replyAdminContact.pending, (state) => {
                state.actionPending = true;
            })
            .addCase(replyAdminContact.fulfilled, (state, action) => {
                state.actionPending = false;
                const updatedContact = action.payload;
                state.contacts = state.contacts.map(c =>
                    (c._id === updatedContact._id || c.id === updatedContact._id) ? updatedContact : c
                );
            })
            .addCase(replyAdminContact.rejected, (state, action) => {
                state.actionPending = false;
                state.error = action.payload;
            });
    }
});

export const { clearAdminError } = adminUserSlice.actions;
export default adminUserSlice.reducer;
